import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Grid, Modal } from '@material-ui/core';

import Icon from '$app-web/components/icon';
import LoadingBar from '$app-web/components/loading-bar';

import classes from './styles.module.scss';

interface Props {
    className?: string;
    variant?: 'modal' | 'parent-overlay' | 'inline';
    loading?: boolean;
    icon?: Array<string> | null;
    loadingText?: string;
    overCallScreen?: boolean;
    error?: boolean;
    errorText?: string;
    showErrorText?: boolean;
    errorHasButton?: boolean;
    errorButtonText?: string;
    onErrorButtonClick?: () => void;
    backdropVariant?: 'light' | 'dark';
}

const Loading = ({
    className,
    variant = 'modal',
    backdropVariant = 'light',
    loading = false,
    icon,
    loadingText,
    overCallScreen = false,
    error = false,
    errorText,
    showErrorText = true,
    errorHasButton = false,
    errorButtonText,
    onErrorButtonClick,
}: Props) => {
    const { t } = useTranslation();

    const content = (
        <Grid className={classes.loadingBarWrapper} container alignItems="center" wrap="nowrap">
            <Grid item xs>
                {icon != null && (
                    <div className={classes.icon}>
                        <Icon icon={icon} size="large" />
                    </div>
                )}
                {loadingText != null && <div className={classes.loadingText}>{loadingText}</div>}
                {loading && <LoadingBar variant="loading" />}
                {error && !errorHasButton && <LoadingBar variant="error" errorText={errorText} showErrorText={showErrorText} />}
                {error && errorHasButton && (
                    <LoadingBar
                        variant="error-with-button"
                        errorText={errorText}
                        errorButtonText={errorButtonText || t('rdash:reload')}
                        onErrorButtonClick={onErrorButtonClick}
                        showErrorText={showErrorText}
                    />
                )}
            </Grid>
        </Grid>
    );

    const show = loading || error;
    const loadingModal =
        variant === 'modal' ? (
            <Modal
                open={show}
                className={className}
                BackdropProps={{
                    className: clsx({
                        [classes.backdropLight]: backdropVariant === 'light',
                        [classes.backdropDark]: backdropVariant === 'dark',
                    }),
                    style: { background: undefined }, // to disable default backdrop bgColor
                }}
            >
                {error ? (
                    <div className={classes.errorWrapper}>
                        <div className={classes.error}>{content}</div>
                    </div>
                ) : (
                    content
                )}
            </Modal>
        ) : null;

    const loadingParentOverlay =
        variant === 'parent-overlay' && show ? (
            <div
                className={clsx(
                    classes.parentOverlay,
                    {
                        [classes.backdropLight]: backdropVariant === 'light',
                        [classes.backdropDark]: backdropVariant === 'dark',
                        [classes.overCallScreen]: overCallScreen,
                    },
                    className
                )}
            >
                {content}
            </div>
        ) : null;

    const loadingInline =
        variant === 'inline' ? <div className={clsx({ [classes.hideInline]: !show }, className)}>{content}</div> : null;

    return loadingModal || loadingParentOverlay || loadingInline;
};

export default Loading;
